import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, MapPin, Maximize2, Home as HomeIcon, Star, CheckCircle2, Phone, Mail, ChevronLeft, X, BedDouble, MessageCircle, Share2 } from 'lucide-react';
import Button from '../components/ui/Button';
import { AGENCY_INFO, formatPrice } from '../constants';
import ShareModal from '../components/ShareModal';
import { fetchPropertyById, fetchProperties, getAgentDisplayInfo, formatNettyRef } from '../lib/nettyService';
import { MOCK_PROPERTIES } from '../data/mocks';
import { trackContactClick, trackPropertyView, trackPageView } from '../lib/analytics';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';
import MortgageCalculator from '../components/MortgageCalculator';
import { TEAM_MEMBERS } from '../data/team';
import { CITY_COORDINATES } from '../data/geocoding';

const SITE_TARGET = import.meta.env.VITE_SITE_TARGET || 'scbi';

const PropertyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [projectLots, setProjectLots] = useState([]);

    // Dynamic agent info from Netty
    const agent = getAgentDisplayInfo(property);
    const agentMatch = TEAM_MEMBERS.find(m => 
        agent?.name && m.name.toLowerCase().includes(agent.name.split(' ')[0].toLowerCase())
    );
    const agentPhoto = agentMatch?.image || agent?.photo || (property?.site_target === 'vanessa' ? '/photo_vanessa.jpg' : AGENCY_INFO.photo);

    // Lightbox Logic
    const allImages = property ? [property.image_url || property.image, ...(property.images || [])].filter(Boolean) : [];
    const currentImageSrc = selectedImage || property?.image_url || property?.image;
    const currentIndex = allImages.indexOf(currentImageSrc);

    const nextImage = (e) => {
        e?.stopPropagation();
        const nextIdx = (currentIndex + 1) % allImages.length;
        setSelectedImage(allImages[nextIdx]);
    };

    const prevImage = (e) => {
        e?.stopPropagation();
        const prevIdx = (currentIndex - 1 + allImages.length) % allImages.length;
        setSelectedImage(allImages[prevIdx]);
    };

    const openLightbox = () => {
        setIsLightboxOpen(true);
        try {
            const elem = document.documentElement;
            if (elem.requestFullscreen) elem.requestFullscreen().catch(() => { });
            else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
        } catch (e) { }
    };

    const closeLightbox = () => {
        setIsLightboxOpen(false);
        try {
            if (document.exitFullscreen) document.exitFullscreen().catch(() => { });
        } catch (e) { }
    };

    useEffect(() => {
        const fetchProperty = async () => {
            setLoading(true);
            try {
                let currentProp = null;
                const data = await fetchPropertyById(id, SITE_TARGET);
                if (data) {
                    currentProp = data;
                } else {
                    const mock = MOCK_PROPERTIES.find(p => p.id === id);
                    if (mock) currentProp = mock;
                }

                if (currentProp) {
                    setProperty(currentProp);
                    setSelectedImage(null); // Reset selected image when changing lot

                    // Fetch related lots in the same program
                    const allProps = await fetchProperties(SITE_TARGET);
                    const sourceProps = allProps && allProps.length > 0 ? allProps : MOCK_PROPERTIES;
                    
                    if (sourceProps) {
                        const { cleanTitleForProject, normalize, hasLotIndicator } = await import('../lib/grouping');
                        const currentCleanTitle = cleanTitleForProject(currentProp.title);
                        const currentNormTitle = normalize(currentCleanTitle);
                        const currentCityNorm = normalize(currentProp.city);
                        
                        const isCurrentLot = hasLotIndicator(currentProp.title);
                        const lat = currentProp.latitude;
                        const lon = currentProp.longitude;
                        const currentCityKey = (currentProp.city || "").toLowerCase().trim();
                        const currentCityCenter = CITY_COORDINATES[currentCityKey] || CITY_COORDINATES[currentCityKey.replace(/\s+/g, '-')] || null;
                        const isCurrentFallbackGPS = currentCityCenter && Math.abs(currentCityCenter[0] - lat) < 0.00001 && Math.abs(currentCityCenter[1] - lon) < 0.00001;
                        const hasGPS = lat && lon && lat !== 0 && lon !== 0 && !isCurrentFallbackGPS;

                        const related = sourceProps.filter(p => {
                            // Only match properties with the same transaction status (sales vs rentals)
                            const pStatus = p.status || 'Vente';
                            const currentStatus = currentProp.status || 'Vente';
                            if (pStatus !== currentStatus) {
                                return false;
                            }

                            const pCleanTitle = cleanTitleForProject(p.title);
                            const pNormTitle = normalize(pCleanTitle);
                            const pCityNorm = normalize(p.city);
                            
                            const pLat = p.latitude;
                            const pLon = p.longitude;
                            const pCityKey = (p.city || "").toLowerCase().trim();
                            const pCityCenter = CITY_COORDINATES[pCityKey] || CITY_COORDINATES[pCityKey.replace(/\s+/g, '-')] || null;
                            const isPFallbackGPS = pCityCenter && Math.abs(pCityCenter[0] - pLat) < 0.00001 && Math.abs(pCityCenter[1] - pLon) < 0.00001;
                            const pHasGPS = pLat && pLon && pLat !== 0 && pLon !== 0 && !isPFallbackGPS;

                            // Match Criterion 1: Same GPS coordinates
                            if (hasGPS && pHasGPS && Math.abs(lat - pLat) < 0.00005 && Math.abs(lon - pLon) < 0.00005) {
                                if (isCurrentLot || hasLotIndicator(p.title)) {
                                    return true;
                                }
                            }
                            // Match Criterion 2: Cleaned Title & City matches + lot indicator exists
                            if (currentNormTitle && pNormTitle === currentNormTitle && pCityNorm === currentCityNorm) {
                                if (isCurrentLot || hasLotIndicator(p.title)) {
                                    return true;
                                }
                            }
                            return false;
                        });

                        // Sort naturally
                        related.sort((a, b) => 
                            (a.title || "").localeCompare(b.title || "", undefined, { numeric: true, sensitivity: 'base' })
                        );

                        if (related.length > 1) {
                            setProjectLots(related);
                        } else {
                            setProjectLots([]);
                        }
                    }
                }
            } catch (e) {
                console.error('[Borbiconi] Error loading property:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchProperty();
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        if (property) {
            const fullTitle = `${property.title} à ${property.city} - Borbiconi Immobilier`;
            document.title = fullTitle;
            trackPropertyView(property);
            trackPageView(window.location.hash, fullTitle);
        }
    }, [property]);

    if (loading) return <div className="p-20 text-center">Chargement...</div>;
    if (!property) return <div className="p-20 text-center">Bien introuvable.</div>;

    return (
        <div className="bg-[#F7F7F7] min-h-screen pb-20 font-sans relative">
            {/* Lightbox Overlay */}
            {isLightboxOpen && (
                <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col md:flex-row items-center justify-center p-4 backdrop-blur-sm" onClick={closeLightbox}>
                    <button onClick={closeLightbox} className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors p-2 z-[101]">
                        <X size={32} />
                    </button>
                    <button onClick={prevImage} className="hidden sm:block absolute left-6 text-white/50 hover:text-white transition-colors p-4 z-[101]">
                        <ChevronLeft size={48} />
                    </button>
                    <img
                        src={currentImageSrc}
                        alt="Plein écran"
                        className="max-w-full max-h-[60vh] sm:max-h-[85vh] md:max-h-[90vh] object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button onClick={nextImage} className="hidden sm:block absolute right-6 text-white/50 hover:text-white transition-colors p-4 z-[101]">
                        <ChevronRight size={48} />
                    </button>
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-sm font-medium tracking-widest uppercase">
                        {currentIndex + 1} / {allImages.length}
                    </div>
                </div>
            )}

            <div className="bg-white border-b border-gray-200 py-4 px-4 sm:px-6 lg:px-8 sticky top-20 z-40 shadow-sm">
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 truncate">
                        <span className="cursor-pointer hover:text-[#EA723D]" onClick={() => navigate('/')}>Accueil</span>
                        <ChevronRight size={14} />
                        <span className="cursor-pointer hover:text-[#EA723D]" onClick={() => navigate('/annonces')}>Biens</span>
                        <ChevronRight size={14} />
                        <span className="font-semibold text-[#2C353D] truncate">{property.title}</span>
                    </div>
                    <button
                        onClick={() => setIsShareModalOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold uppercase tracking-wider text-[#2C353D] hover:bg-gray-50 transition-colors shrink-0"
                    >
                        <Share2 size={14} className="text-[#EA723D]" /> Partager
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6">
                        {/* Lot Switcher Bar */}
                        {projectLots.length > 0 && (() => {
                            const currentIdx = projectLots.findIndex(lot => lot.id === property.id);
                            return (
                                <div className="bg-white p-4 shadow-sm rounded-2xl border border-gray-150 flex flex-col sm:flex-row items-center justify-between gap-4 w-full max-w-full min-w-0">
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 bg-[#EA723D]/10 rounded-xl text-[#EA723D] shrink-0">
                                            <HomeIcon size={24} />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Ce bien fait partie d'un programme</p>
                                            <p className="text-sm font-extrabold text-[#2C353D]">{projectLots.length} lots disponibles {property.status === 'Location' ? 'à la location' : 'à la vente'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
                                        <button
                                            disabled={currentIdx <= 0}
                                            onClick={() => navigate(`/property/${projectLots[currentIdx - 1].id}`)}
                                            className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white text-[#2C353D] transition-colors shrink-0 shadow-sm"
                                            title="Lot précédent"
                                        >
                                            <ChevronLeft size={18} />
                                        </button>
                                        
                                        <select
                                            value={property.id}
                                            onChange={(e) => navigate(`/property/${e.target.value}`)}
                                            className="bg-gray-50 border border-gray-200 text-[#2C353D] text-xs font-bold rounded-xl focus:ring-[#EA723D] focus:border-[#EA723D] block w-full sm:w-64 min-w-0 max-w-full p-2.5 outline-none cursor-pointer shadow-sm"
                                        >
                                            {projectLots.map((lot, idx) => {
                                                const lotName = lot.title.split(' - ').slice(1).join(' - ') || lot.title;
                                                return (
                                                    <option key={lot.id} value={lot.id}>
                                                        Lot {idx + 1} : {lotName} ({lot.surface || lot.surface_terrain || '-'}m² - {formatPrice(lot.price)})
                                                    </option>
                                                );
                                            })}
                                        </select>

                                        <button
                                            disabled={currentIdx < 0 || currentIdx >= projectLots.length - 1}
                                            onClick={() => navigate(`/property/${projectLots[currentIdx + 1].id}`)}
                                            className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white text-[#2C353D] transition-colors shrink-0 shadow-sm"
                                            title="Lot suivant"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}

                        <div className="bg-white p-2 shadow-sm rounded-2xl overflow-hidden border border-gray-100">
                            {/* Main Image */}
                            <div className="relative aspect-video rounded-xl overflow-hidden mb-2 bg-gray-100 cursor-pointer group" onClick={openLightbox}>
                                <img
                                    src={getOptimizedImageUrl(selectedImage || property.image_url || property.image, 1200)}
                                    alt={property.title}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>

                            {/* Gallery */}
                            {allImages.length > 1 && (
                                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                    {allImages.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedImage(img)}
                                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${currentImageSrc === img ? 'border-[#EA723D]' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                        >
                                            <img src={getOptimizedImageUrl(img, 200)} alt={`Vue ${idx + 1}`} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="bg-white p-6 md:p-8 shadow-sm rounded-2xl border border-gray-100">
                            <div className="flex justify-between items-start mb-8 gap-4">
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-black text-[#2C353D] leading-tight mb-2">{property.title}</h1>
                                    <p className="text-gray-500 flex items-center gap-1 font-medium"><MapPin size={18} className="text-[#EA723D]" /> {property.city} ({property.zip_code || ''})</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl md:text-3xl font-black text-[#2C353D]">{formatPrice(property.price)}</div>
                                    <span className={`inline-block text-white text-[10px] font-bold px-3 py-1 uppercase rounded-full shadow-md mt-2 ${property.status === 'Location' ? 'bg-[#EA723D]' : 'bg-[#2C353D]'}`}>
                                        {property.status}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 mb-10">
                                <div className="text-center p-3 bg-[#F7F7F7] rounded-xl">
                                    <HomeIcon size={20} className="mx-auto text-[#EA723D] mb-1" />
                                    <span className="block text-sm font-black text-[#2C353D] truncate">{property.type}</span>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Type</span>
                                </div>
                                <div className="text-center p-3 bg-[#F7F7F7] rounded-xl">
                                    <Maximize2 size={20} className="mx-auto text-[#EA723D] mb-1" />
                                    <span className="block text-sm font-black text-[#2C353D]">{property.surface || property.surface_terrain || '-'} m²</span>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Surface</span>
                                </div>
                                <div className="text-center p-3 bg-[#F7F7F7] rounded-xl">
                                    <BedDouble size={20} className="mx-auto text-[#EA723D] mb-1" />
                                    <span className="block text-sm font-black text-[#2C353D]">{property.rooms || property.nb_pieces || '-'}</span>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">Pièces</span>
                                </div>
                            </div>

                            <h3 className="font-bold text-xl mb-4 text-[#2C353D]">Description</h3>
                            <div className="text-gray-600 leading-relaxed mb-10 text-justify whitespace-pre-line text-base md:text-lg">
                                {property.description}
                            </div>

                            {projectLots.length > 0 && (
                                <div className="mb-10 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-md w-full max-w-full min-w-0">
                                    <div className="bg-[#2C353D] text-white p-5">
                                        <h3 className="font-black text-lg flex items-center gap-2">
                                            🏢 Lots disponibles dans cette résidence
                                        </h3>
                                        <p className="text-xs text-white/75 mt-1">
                                            Découvrez et comparez les appartements disponibles dans le même programme à {property.city}.
                                        </p>
                                    </div>
                                    
                                    {/* Desktop Table View */}
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 border-b border-gray-200 text-[10px] text-gray-500 uppercase font-black tracking-widest">
                                                    <th className="p-4 pl-6">Lot</th>
                                                    <th className="p-4">Surface</th>
                                                    <th className="p-4">Pièces</th>
                                                    <th className="p-4">Prix</th>
                                                    <th className="p-4 text-right pr-6">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {projectLots.map(lot => {
                                                    const isCurrent = lot.id === property.id;
                                                    const lotName = lot.title.split(' - ').slice(1).join(' - ') || lot.title;
                                                    
                                                    return (
                                                        <tr 
                                                            key={lot.id} 
                                                            className={`transition-colors ${isCurrent ? 'bg-orange-50/30 border-l-4 border-[#EA723D]' : 'hover:bg-gray-50/50'}`}
                                                        >
                                                            <td className="p-4 pl-6">
                                                                <span className={`font-bold text-sm ${isCurrent ? 'text-[#EA723D] font-extrabold' : 'text-[#2C353D]'}`}>
                                                                    {lotName}
                                                                </span>
                                                                {isCurrent && (
                                                                    <span className="ml-2 inline-block bg-[#EA723D]/10 text-[#EA723D] text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border border-[#EA723D]/25">
                                                                        Lot actuel
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="p-4 text-xs font-bold text-gray-600">
                                                                {lot.surface || lot.surface_terrain || '-'} m²
                                                            </td>
                                                            <td className="p-4 text-xs font-bold text-gray-600">
                                                                {lot.rooms || lot.nb_pieces || '-'} ch.
                                                            </td>
                                                            <td className="p-4 text-sm font-extrabold text-[#2C353D]">
                                                                {formatPrice(lot.price)}
                                                            </td>
                                                            <td className="p-4 text-right pr-6">
                                                                {isCurrent ? (
                                                                    <span className="text-xs font-black uppercase tracking-wider text-gray-400">
                                                                        En cours de visite
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => navigate(`/property/${lot.id}`)}
                                                                        className="px-4 py-2 text-xs font-black uppercase tracking-wider text-white bg-[#2C353D] hover:bg-[#EA723D] rounded-xl shadow-md transition-colors"
                                                                    >
                                                                        Voir ce lot
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Cards List View */}
                                    <div className="block md:hidden divide-y divide-gray-100">
                                        {projectLots.map(lot => {
                                            const isCurrent = lot.id === property.id;
                                            const lotName = lot.title.split(' - ').slice(1).join(' - ') || lot.title;
                                            
                                            return (
                                                <div 
                                                    key={lot.id} 
                                                    className={`p-4 transition-colors ${isCurrent ? 'bg-orange-50/25 border-l-4 border-[#EA723D]' : 'hover:bg-gray-50/50'}`}
                                                >
                                                    <div className="flex justify-between items-start mb-2 gap-2">
                                                        <div className="min-w-0">
                                                            <span className={`font-bold text-sm block ${isCurrent ? 'text-[#EA723D] font-extrabold' : 'text-[#2C353D]'}`}>
                                                                {lotName}
                                                            </span>
                                                            {isCurrent && (
                                                                <span className="mt-1 inline-block bg-[#EA723D]/10 text-[#EA723D] text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#EA723D]/25">
                                                                    Lot actuel
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-sm font-extrabold text-[#2C353D] whitespace-nowrap">
                                                            {formatPrice(lot.price)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center text-xs text-gray-500 font-bold mt-3">
                                                        <div>
                                                            <span>{lot.surface || lot.surface_terrain || '-'} m²</span>
                                                            <span className="mx-2 text-gray-300">•</span>
                                                            <span>{lot.rooms || lot.nb_pieces || '-'} ch.</span>
                                                        </div>
                                                        <div>
                                                            {isCurrent ? (
                                                                <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                                                                    En cours
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    onClick={() => navigate(`/property/${lot.id}`)}
                                                                    className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-white bg-[#2C353D] hover:bg-[#EA723D] rounded-lg shadow-md transition-colors"
                                                                >
                                                                    Voir ce lot
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* DPE Indicators */}
                            {(property.dpe_energy || property.dpe_ges) && (
                                <>
                                    <h3 className="font-bold text-xl mb-6 text-[#2C353D]">Performance Énergétique</h3>
                                    <div className="grid grid-cols-2 gap-4 mb-10">
                                        <div className="bg-[#F8F9FA] p-4 rounded-xl border border-gray-100 flex flex-col items-center">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase mb-2">Classe Énergie</span>
                                            <div className={`w-12 h-12 flex items-center justify-center rounded-lg text-white font-black text-xl shadow-lg ${property.dpe_energy === 'A' ? 'bg-green-500' : property.dpe_energy === 'B' ? 'bg-green-400' : property.dpe_energy === 'C' ? 'bg-yellow-400' : property.dpe_energy === 'D' ? 'bg-orange-400' : 'bg-red-500'}`}>
                                                {property.dpe_energy || '?'}
                                            </div>
                                        </div>
                                        <div className="bg-[#F8F9FA] p-4 rounded-xl border border-gray-100 flex flex-col items-center">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase mb-2">Classe Climat</span>
                                            <div className={`w-12 h-12 flex items-center justify-center rounded-lg text-white font-black text-xl shadow-lg ${property.dpe_ges === 'A' ? 'bg-purple-300' : 'bg-purple-600'}`}>
                                                {property.dpe_ges || '?'}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            <h3 className="font-bold text-xl mb-4 text-[#2C353D]">Les points clés</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {property.features ? property.features.map((f, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 bg-[#F7F7F7] rounded-xl text-sm font-bold text-gray-700">
                                        <CheckCircle2 size={18} className="text-[#EA723D] shrink-0" /> {f}
                                    </div>
                                )) : (
                                    <p className="text-gray-400 text-sm italic">Consultez-nous pour plus d'informations.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-white p-8 shadow-xl rounded-2xl sticky top-36 border-t-4 border-[#2C353D]">
                            <div className="flex items-center gap-4 mb-8">
                                <img src={agentPhoto} className="w-16 h-16 rounded-full object-cover object-top border-4 border-[#F7F7F7] shadow-sm" alt={agent?.name} />
                                <div>
                                    <p className="font-black text-[#2C353D] text-lg leading-tight">{agent?.name || AGENCY_INFO.name}</p>
                                    <p className="text-xs text-[#EA723D] font-bold uppercase tracking-wide">
                                        {(agent?.name?.toUpperCase().includes('BORBICONI') ||
                                          agent?.name?.toUpperCase().includes('CHRISTOPHE'))
                                            ? "Agent Immobilier"
                                            : (agent?.name?.toUpperCase().includes('PINEIRO') || 
                                          agent?.name?.toUpperCase().includes('PEDESINI') || 
                                          agent?.name?.toUpperCase().includes('TANCREDI') || 
                                          agent?.name?.toUpperCase().includes('JULIA') || 
                                          agent?.name?.toUpperCase().includes('EMELINE') || 
                                          agent?.name?.toUpperCase().includes('ANNE-CATHERINE')) 
                                            ? "Conseillère Immobilier" 
                                            : "Conseiller Immobilier"}
                                    </p>
                                </div>
                            </div>

                            {/* Agent Bio / Description */}
                            {(() => {
                                const agentName = agent?.name;
                                if (!agentName) return null;

                                const firstName = agentName.split(' ')[0].toLowerCase();
                                const matchedMember = TEAM_MEMBERS.find(m => 
                                    m.name.toLowerCase().includes(firstName)
                                );
                                
                                if (matchedMember) {
                                    return (
                                        <p className="text-gray-500 text-xs italic mb-6 leading-relaxed border-l-2 border-[#EA723D]/20 pl-4">
                                            "{matchedMember.description}"
                                        </p>
                                    );
                                }
                                return null;
                            })()}

                            <div className="space-y-3">
                                <Button
                                    variant="custom"
                                    className="w-full bg-[#2C353D] hover:bg-[#1a2126] text-white flex items-center justify-center gap-3 py-4 rounded-xl text-sm font-bold transition-all hover:scale-[1.02]"
                                    onClick={() => {
                                        trackContactClick('phone', property.title);
                                        const phone = (agent?.phone || AGENT_INFO.phone).replace(/\s/g, '');
                                        window.location.href = `tel:${phone}`;
                                    }}
                                >
                                    <Phone size={18} /> {agent?.phone || AGENT_INFO.phone}
                                </Button>
                                <Button
                                    variant="custom"
                                    onClick={() => {
                                        trackContactClick('email_page', property.title);
                                        navigate('/contact', { state: { propertyTitle: property.title } });
                                    }}
                                    className="w-full flex items-center justify-center gap-3 py-4 rounded-xl text-sm bg-white border-2 border-gray-100 hover:border-gray-200 text-[#2C353D] font-bold transition-all hover:scale-[1.02]"
                                >
                                    <Mail size={18} /> ENVOYER UN EMAIL
                                </Button>
                                <Button
                                    variant="custom"
                                    className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white flex items-center justify-center gap-3 py-4 rounded-xl text-sm font-bold transition-all hover:scale-[1.02]"
                                    onClick={() => {
                                        trackContactClick('whatsapp', property.title);
                                        let phone = (agent?.phone || AGENT_INFO.phone).replace(/\s/g, '').replace(/[^0-9]/g, '');
                                        // Convert French number to international format (add +33 if not present)
                                        if (phone.startsWith('0')) {
                                            phone = '33' + phone.substring(1);
                                        } else if (!phone.startsWith('33')) {
                                            phone = '33' + phone;
                                        }
                                        const message = `Bonjour, Je souhaiterais avoir des informations sur le bien : ${property.title} (${property.city})`;
                                        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
                                    }}
                                >
                                    <MessageCircle size={18} /> WHATSAPP
                                </Button>
                                <Button
                                    variant="custom"
                                    className="w-full bg-gray-100 hover:bg-gray-200 text-[#2C353D] flex items-center justify-center gap-3 py-4 rounded-xl text-sm font-bold transition-all hover:scale-[1.02]"
                                    onClick={() => setIsShareModalOpen(true)}
                                >
                                    <Share2 size={18} /> PARTAGER & QR CODE
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Simulateur de Prêt & Financement (Biens à la vente uniquement) */}
                {property.status?.toLowerCase() !== 'location' && (
                    <MortgageCalculator
                        propertyPrice={property.price}
                        propertyTitle={property.title}
                        propertyRef={property.netty_ref}
                        agentPhone={agent?.phone}
                    />
                )}
            </div>
            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                propertyTitle={property.title}
                propertyUrl={window.location.href}
            />
        </div>
    );
};

export default PropertyDetail;
